import { type Epic, ofType } from 'redux-observable';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import type { AjaxResponse } from 'rxjs/ajax';
import { execution$, dealReply$, executionError$ } from '@/services/streams.service';
import { executeAmericanForwardRfs, executeCashRfs, executeSwapRfs } from '@/services/trade.service';
import type { AppAction } from '../app.actions';
import type { AppState } from '../app.state';
import { backOfficeValidatedAction, type ExecuteAction, executedAction, executionFailedAction, executionSentAction, timeoutedDealAction } from '../state/rfs/rfs.actions';
import { getSpotPrecision } from '@/store/state/rfs/rfs.selectors';
import { convertToPointsWithPrecision } from '@/utils/format';
import { logger } from '@/logging/logger';
import type { DealRejected, DealReply, FillReport } from '@/models/streaming';
import { getNewRfsId } from './request-for-stream.epic';

const HTTP_TIMEOUT = 408;
const ANY_HTTP_ERROR = 408;

const handleResponseFromSignalR = ([executionResult, state]: [FillReport | DealRejected | DealReply, AppState]) => {
  logger.logInformation('handleResponseFromSignalR - executionResult {executionResult} state {status}', executionResult, {
    status: state.rfs.status,
  });

  if (executionResult.type === 'DEAL.REJECTED') {
    return executionFailedAction();
  }
  if (executionResult.type === 'FILL.REPORT') {
    return backOfficeValidatedAction(executionResult.tradeIds[0]);
  }
  return executedAction();
};

export const executeEpic =
  (
    fillReportStreaming$ = execution$,
    dealReplyStreaming$ = dealReply$,
    dealRejectedStreaming$ = executionError$,
    requestForCashExecution = executeCashRfs,
    requestForSwapExecution = executeSwapRfs,
    requestForAmericanForwardExecution = executeAmericanForwardRfs,
  ): Epic<AppAction, AppAction, AppState> =>
  (action$, state$) =>
    action$.pipe(ofType<AppAction, ExecuteAction>('EXECUTE')).pipe(
      withLatestFrom(state$),
      switchMap(([, { rfs, streaming, user }]) => {
        const handleMultipassReplies$ = merge(fillReportStreaming$(), dealRejectedStreaming$(), dealReplyStreaming$()).pipe(
          withLatestFrom(state$),
          tap(([executionResult, state]) => {
            logger.logInformation('Before filter - executionResult {executionResult} state {status}', executionResult, {
              status: state.rfs.status,
            });
          }),
          filter(
            ([executionResult, state]) =>
              (state.rfs.status === 'execution-timeout' || state.rfs.status === 'executing' || state.rfs.status === 'executed') && state.rfs.id === executionResult.rfsId,
          ),
          tap(([executionResult, state]) =>
            logger.logInformation('executionResult {executionResult} state {status}', executionResult, {
              status: state.rfs.status,
            }),
          ),
          map(handleResponseFromSignalR),
        );

        const getExecutionRequest = () => {
          if (rfs.status !== 'executing') {
            return EMPTY;
          }
          if (rfs.product === 'FxAmericanForward') {
            const email = user.currentUser?.email;
            return requestForAmericanForwardExecution(rfs.id, rfs.quoteId, streaming.connectionId!, rfs.clientTimestamp, rfs.serverTimestamp, email!).pipe(
              catchError((e: AjaxResponse) => of(e)),
            );
          }

          if (rfs.product === 'FxRollover') {
            return requestForSwapExecution(
              rfs.id,
              rfs.quoteId,
              rfs.spotRate,
              +rfs.nearRate.toFixed(getSpotPrecision(rfs)),
              +rfs.rolloverRate.toFixed(getSpotPrecision(rfs)),
              rfs.nearPoints,
              rfs.farPoints,
              rfs.farPoints,
              rfs.sgWay,
              streaming.connectionId!,
              rfs.clientTimestamp,
              rfs.serverTimestamp,
            );
          }

          const points = rfs.product === 'FxFwd' ? rfs.forwardPoints : rfs.product === 'FxPredeliver' ? convertToPointsWithPrecision(rfs.rolloverPoints, getSpotPrecision(rfs)) : 0;

          return requestForCashExecution(rfs.id, rfs.quoteId, rfs.spotRate, points, rfs.sgWay, streaming.connectionId!, rfs.clientTimestamp, rfs.serverTimestamp).pipe(
            catchError((e: AjaxResponse) => of(e)),
          );
        };

        const executionRequest$ = getExecutionRequest();

        const executeRequest$ = executionRequest$.pipe(
          mergeMap((resultFromLastPipeFromAjax) =>
            resultFromLastPipeFromAjax.status === HTTP_TIMEOUT
              ? of(timeoutedDealAction())
              : resultFromLastPipeFromAjax.status >= ANY_HTTP_ERROR
                ? of(executionFailedAction())
                : of(executionSentAction()).pipe(
                    takeUntil(state$.pipe(filter((_) => rfs.status === 'backOfficeValidated' || rfs.status === 'execution-timeout' || rfs.status === 'execution-failed'))),
                  ),
          ),
        );

        return merge(handleMultipassReplies$, executeRequest$);
      }),
    );
